/**
 * Participant Shell - Tarbiyah Heritage Theme
 *
 * Main layout for authenticated participant user. Features:
 * - Scholarly calm (deep greens + warm golds)
 * - Top navigation bar with responsive collapse to bottom tabs on mobile
 * - Glassmorphism header with user profile
 * - All Lucide icons (no emoji)
 */

/**
 * Participant Shell - Tarbiyah Heritage Theme
 *
 * Main layout for authenticated participant user. Features:
 * - Scholarly calm (deep greens + warm golds)
 * - Top navigation bar with responsive collapse to bottom tabs on mobile
 * - Glassmorphism header with user profile
 * - All Lucide icons (no emoji)
 */

import { useState } from 'react';
import { useLocation, NavLink } from 'react-router';
import { useAuthStore } from '../store/auth';
import {
  LayoutDashboard,
  BookOpen,
  Award,
  LogOut,
  ScrollText,
  BadgeCheck,
  Megaphone,
  History,
  Menu,
  X,
} from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { useAutoLogout } from '../hooks/useAutoLogout';
import { InactivityWarningModal } from './InactivityWarningModal';
import { NotificationPopover } from './NotificationPopover';
import { AnnouncementBanner } from './AnnouncementBanner';

const navItems = [
  { path: '/', label: copy.shell.dashboard, icon: LayoutDashboard },
  { path: '/programs', label: copy.shell.myPrograms, icon: BookOpen },
  { path: '/announcements', label: copy.shell.announcements, icon: Megaphone },
  { path: '/results', label: copy.shell.results, icon: Award },
  { path: '/transcripts', label: copy.shell.transcripts, icon: ScrollText },
  { path: '/legacy-transcripts', label: copy.shell.legacyTranscripts, icon: History },
  { path: '/certificates', label: copy.shell.certificates, icon: BadgeCheck },
];

const mobilePrimaryNavItems = navItems.filter(item =>
  ['/', '/programs', '/transcripts', '/legacy-transcripts'].includes(item.path)
);
const mobileOverflowNavItems = navItems.filter(
  item => !mobilePrimaryNavItems.some(primaryItem => primaryItem.path === item.path)
);

export function ParticipantShell({ children }: { children: React.ReactNode }) {
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { isWarningOpen, countdown, stayLoggedIn, logoutNow } = useAutoLogout();

  return (
    <div className="flex flex-col min-h-screen bg-surface text-on-surface font-body overflow-x-hidden">
      {/* Top Navigation Bar (Glassmorphism) */}
      <nav className="fixed top-0 z-50 flex h-16 w-full max-w-full items-center justify-between bg-surface-container-low/90 px-4 shadow-level1 backdrop-blur-xl sm:px-6">
        <div className="flex items-center gap-4">
          <img
            alt="Tarbiyah Heritage Logo"
            className="h-10 w-auto object-contain"
            src="/logo.png"
          />
          <div className="mx-2 hidden items-center gap-1 rounded-2xl bg-surface-container p-1 lg:flex">
            {navItems.map(({ path, label }) => {
              const isActive =
                location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
              return (
                <NavLink
                  key={path}
                  to={path}
                  className={`rounded-xl px-3 py-2 text-xs font-label font-bold no-underline transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${
                    isActive
                      ? 'bg-surface-container-lowest text-primary shadow-level1'
                      : 'text-on-surface-variant hover:bg-surface-container-high hover:text-primary'
                  }`}
                >
                  <Bilingual {...label} />
                </NavLink>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Language Switcher */}
          <div className="flex items-center rounded-lg bg-surface-container p-1">
            <button
              onClick={() => setLanguage('id')}
              className={`px-2.5 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-[11px] font-bold rounded transition-all ${
                language === 'id'
                  ? 'bg-primary text-on-primary shadow-level1'
                  : 'text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              ID
            </button>
            <button
              onClick={() => setLanguage('ar')}
              className={`px-2.5 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-[11px] font-bold rounded transition-all ${
                language === 'ar'
                  ? 'bg-primary text-on-primary shadow-level1'
                  : 'text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              AR
            </button>
          </div>

          <NotificationPopover />

          <NavLink
            to="/profile"
            className="flex items-center gap-3 no-underline text-on-surface hover:text-primary group"
          >
            <span className="hidden sm:inline text-sm font-label font-medium tracking-wide text-on-surface-variant group-hover:text-primary transition-colors">
              {user?.name || <Bilingual {...copy.shell.student} />}
            </span>
            <div className="w-9 h-9 rounded-full bg-surface-container-highest flex items-center justify-center text-primary font-headline font-semibold text-sm overflow-hidden shadow-level1 group-hover:scale-105 transition-transform">
              {(user?.name || 'P')[0].toUpperCase()}
            </div>
          </NavLink>

          <button
            onClick={() => logout()}
            className="p-2 text-on-surface-variant hover:text-error transition-colors flex items-center gap-2 font-label font-bold text-xs uppercase tracking-widest"
            title={t(copy.shell.logout)}
            aria-label={t(copy.shell.logout)}
          >
            <LogOut size={18} />
            <span className="hidden lg:inline">
              <Bilingual {...copy.shell.logout} />
            </span>
          </button>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-1 pt-16 pb-20 lg:pb-8">
        <AnnouncementBanner />
        <div className="p-4 sm:p-6 md:p-10 max-w-7xl mx-auto animate-fadeIn">{children}</div>
      </main>

      {isMobileMenuOpen && (
        <div
          id="participant-mobile-more-menu"
          className="fixed inset-x-4 bottom-24 z-50 rounded-3xl bg-surface-container-lowest p-3 shadow-level4 lg:hidden"
        >
          <div className="flex items-center justify-between gap-3 px-2 pb-2">
            <span className="text-xs font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
              <Bilingual {...copy.shell.more} />
            </span>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label={t(copy.common.close)}
              className="grid min-h-11 min-w-11 place-items-center rounded-xl text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <X size={18} />
            </button>
          </div>
          <div className="grid gap-2">
            {mobileOverflowNavItems.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex min-h-11 items-center gap-3 rounded-xl px-4 py-3 text-sm font-label font-bold no-underline transition-colors active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                    isActive
                      ? 'bg-primary text-on-primary shadow-level1'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high hover:text-primary'
                  }`
                }
              >
                <Icon size={18} />
                <span className="whitespace-nowrap">
                  <Bilingual {...label} />
                </span>
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation (visible only on mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-20 items-center justify-around bg-surface-container-low/95 px-1 shadow-level4 backdrop-blur-xl safe-area-inset-bottom lg:hidden">
        {mobilePrimaryNavItems.map(({ path, label, icon: Icon }) => {
          const isActive =
            location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
          return (
            <NavLink
              key={path}
              to={path}
              className={`flex min-h-11 min-w-0 max-w-[72px] flex-1 flex-col items-center gap-0.5 rounded-xl px-2 py-2 no-underline transition-colors
                ${
                  isActive
                    ? 'bg-primary/10 text-primary font-bold'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              title={t(label)}
            >
              <Icon size={20} />
              <span className="text-[10px] font-label font-semibold leading-tight text-center truncate w-full">
                <Bilingual {...label} />
              </span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(open => !open)}
          aria-expanded={isMobileMenuOpen}
          aria-controls="participant-mobile-more-menu"
          className={`flex min-h-11 min-w-0 max-w-[72px] flex-1 flex-col items-center gap-0.5 rounded-xl px-2 py-2 text-[10px] font-label font-semibold transition-colors active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
            isMobileMenuOpen
              ? 'bg-primary/10 text-primary'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          <Menu size={20} />
          <span className="w-full truncate leading-tight">
            <Bilingual {...copy.shell.more} />
          </span>
        </button>
      </nav>

      <InactivityWarningModal
        isOpen={isWarningOpen}
        countdown={countdown}
        onStayLoggedIn={stayLoggedIn}
        onLogoutNow={logoutNow}
      />
    </div>
  );
}

/*
// @ts-expect-error
function getCurrentPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    '/': 'Dashboard',
    '/programs': 'My Programs',
    '/results': 'Learning Results',
    '/profile': 'Profile',
  };

  if (pathname in titles) return titles[pathname];
  if (pathname.startsWith('/programs/')) return 'Program Details';
  if (pathname.startsWith('/materials/')) return 'Material Viewer';
  if (pathname.startsWith('/evaluations/')) return 'Evaluation';

  return 'BID Learning Portal';
}
*/
